/* eslint-disable react-hooks/exhaustive-deps */
import { Mutation } from "@apollo/client/react/components";
import NomadCashbackAd from "components/advertisements/NomadCashbackAd";
import ToggleBookmarkButton from "components/bookmarks/ToggleBookmarkButton";
import YouTubeVideos from "components/company/v2/YouTubeVideos";
import BackToTop from "components/qol/BackToTop";
import Rating from "components/rating/Rating";
import TextTooltip from "components/shared/TextTooltip";
import TwoPassRender from "components/shared/TwoPassRender";
import formatMoneyValue from "components/utils/formatMoneyValue";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import { shades } from "lib/miolo";
import CloseNomadPartnership from "lib/queries/CloseNomadPartnership";
import type {
  ICloseNomadPartnershipMutation,
  ICloseNomadPartnershipMutationVariables,
} from "lib/queries/__generated__/CloseNomadPartnership.generated";
import type { ISelfieQuery } from "lib/queries/__generated__/Selfie.generated";
import stringHashCode from "lib/utils/stringHashCode";
import { default as Head } from "next/head";
import numeral from "numeral";
import { useEffect } from "react";
import { useAmplitude } from "../../lib/amplitude/Amplitude";
import useSelfie from "../../lib/hooks/useSelfie";
import CloseRemessaOnlineDiscount from "../../lib/queries/CloseRemessaOnlineDiscount";
import type {
  ICloseRemessaOnlineDiscountMutation,
  ICloseRemessaOnlineDiscountMutationVariables,
} from "../../lib/queries/__generated__/CloseRemessaOnlineDiscount.generated";
import { IAssetNamespace, IAssetType } from "../../types/graphqlTypes";
import RemessaOnlineDiscountAdvertisement from "../advertisements/RemessaOnlineDiscountAdvertisement";
import AssetDiscussion from "../discussions/AssetDiscussion";
import AssetDiscussionGridContainer from "../discussions/AssetDiscussionGridContainer";
import Liquidity from "../liquidity/Liquidity";
import * as palette from "../palette";
import NASDAQ from "../svg/NASDAQ";
import NYSE from "../svg/NYSE";
import AmericanCompanyFinancials from "./financials/american/AmericanCompanyFinancials";
import IndustrySector from "./IndustrySector";
import AmericanCompanySections from "./sections/AmericanCompanySections";

interface IAmericanCompanyProps {
  company: IAmericanCompanyIssuerTopLevelFragment;
  tickerSymbol: string;
}

function decideBetweenNomadOrRemessaAdToDisplay(user: NonNullable<ISelfieQuery["selfie"]>) {
  const hashCode = stringHashCode(user._id);
  if (
    !user.settings.advertisements.hasClosedNomadPartnership &&
    !user.settings.advertisements.hasClosedRemessaOnlineDiscount
  ) {
    return hashCode % 2 === 0 ? "NOMAD" : "REMESSA";
  }
  if (
    user.settings.advertisements.hasClosedRemessaOnlineDiscount &&
    !user.settings.advertisements.hasClosedNomadPartnership
  ) {
    return "NOMAD";
  }
  if (
    user.settings.advertisements.hasClosedNomadPartnership &&
    !user.settings.advertisements.hasClosedRemessaOnlineDiscount
  ) {
    return "REMESSA";
  }
  return false;
}

export default function AmericanCompany({ company, tickerSymbol }: IAmericanCompanyProps) {
  const user = useSelfie();
  const amplitude = useAmplitude();

  useEffect(() => {
    amplitude.logEvent("Viewed issuer", {
      category: "Stocks",
      issuerId: company._id,
      tickerSymbol,
      name: company.name,
      sector: company.sector,
    });
  }, [company._id]);

  if (!user) {
    return null;
  }

  const nomadOrRemessaAdToDisplay = decideBetweenNomadOrRemessaAdToDisplay(user);
  const security = company.securities.find((candidate) => {
    return candidate.tickerSymbol === tickerSymbol;
  });

  return (
    <div
      css={{
        display: "grid",
        gridRowGap: 32,
        paddingTop: 36,
        paddingBottom: 36,
      }}
    >
      <Head>
        <title>{`${tickerSymbol} | Fundamentei`}</title>
      </Head>

      <BackToTop />

      {nomadOrRemessaAdToDisplay === "NOMAD" && (
        <div
          css={{
            maxWidth: 1140,
            margin: "0px auto",
          }}
        >
          <Mutation<ICloseNomadPartnershipMutation, ICloseNomadPartnershipMutationVariables>
            mutation={CloseNomadPartnership}
          >
            {(closeNomadPartnership) => {
              return <NomadCashbackAd onClose={closeNomadPartnership} />;
            }}
          </Mutation>
        </div>
      )}

      {nomadOrRemessaAdToDisplay === "REMESSA" && (
        <div
          css={{
            maxWidth: 1140,
            margin: "0px auto",
          }}
        >
          <Mutation<ICloseRemessaOnlineDiscountMutation, ICloseRemessaOnlineDiscountMutationVariables>
            mutation={CloseRemessaOnlineDiscount}
          >
            {(closeRemessaOnlineDiscount) => {
              return <RemessaOnlineDiscountAdvertisement onClose={closeRemessaOnlineDiscount} />;
            }}
          </Mutation>
        </div>
      )}

      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gridColumnGap: 30,
          gridRowGap: 52,
          alignItems: "center",
          margin: "0px auto",
          width: 1140,
        }}
      >
        <div
          css={{
            gridColumn: "2 / span 6",
            boxShadow: "0 4px 6px 0 #33333333",
            backgroundColor: "#ffffff",
            borderRadius: 4,
          }}
        >
          <div
            css={{
              padding: 24,
              display: "grid",
              gridRowGap: 20,
            }}
          >
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridRowGap: 12,
                }}
              >
                <div>
                  <div className="flex space-x-2">
                    <div>
                      <h1
                        css={{
                          margin: 0,
                          fontSize: 21,
                          fontWeight: "bold",
                          lineHeight: 1.33,
                          letterSpacing: 0.9,
                          color: palette.bigStone,
                          display: "flex",
                        }}
                      >
                        {company.name}
                      </h1>

                      <h3
                        css={{
                          margin: 0,
                          fontSize: 16,
                          fontWeight: "bold",
                          lineHeight: 1.33,
                          letterSpacing: 0.9,
                          color: palette.blueBayoux,
                          display: "flex",
                        }}
                      >
                        {tickerSymbol}
                      </h3>
                    </div>
                    <div>
                      <ToggleBookmarkButton issuer={company} viewerDidBookmark={company.viewerDidBookmark} />
                    </div>
                  </div>

                  {company.irs && (
                    <span
                      css={{
                        fontSize: 13,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.lynch,
                        fontWeight: 500,
                      }}
                    >
                      {`IRS: ${company.irs}`}
                    </span>
                  )}
                </div>

                {company.isAmericanDepositaryReceipt && (
                  <div>
                    <TextTooltip
                      id="tooltips/americanDepositaryReceipt"
                      placement="top"
                      content={() => {
                        if (security && security.depositoryReceiptRatio) {
                          return `Ratio: ${security.depositoryReceiptRatio}`;
                        }
                        return "American Depositary Receipt";
                      }}
                    >
                      {({ accessibility, bind, ref }) => (
                        <span
                          {...accessibility}
                          {...bind}
                          ref={ref}
                          title="American Depositary Receipt"
                          css={{
                            backgroundColor: palette.selago,
                            margin: 0,
                            fontSize: 12,
                            fontWeight: "bold",
                            lineHeight: 1.33,
                            letterSpacing: 0.9,
                            color: palette.mariner,
                            display: "inline-flex",
                            borderRadius: 4,
                            padding: "4px 8px",
                          }}
                        >
                          ADR
                        </span>
                      )}
                    </TextTooltip>
                  </div>
                )}
              </div>

              {company.standardLogo && (
                <img
                  src={company.standardLogo.url}
                  width={158}
                  height={48}
                  alt={company.name}
                  css={{
                    objectFit: "contain",
                    objectPosition: "right",
                    height: 48,
                  }}
                />
              )}
            </div>

            <div>
              <div
                css={{
                  display: "grid",
                  gridAutoFlow: "column",
                  gridAutoColumns: "max-content",
                  gridColumnGap: 20,
                }}
              >
                {company.foundedYear && (
                  <div
                    css={{
                      display: "grid",
                      gridRowGap: 4,
                    }}
                  >
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.mineShaft,
                      }}
                    >
                      Founded
                    </div>

                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.lynch,
                      }}
                    >
                      {company.foundedYear}
                    </div>
                  </div>
                )}

                {company.ipoDate && (
                  <div
                    css={{
                      display: "grid",
                      gridRowGap: 4,
                    }}
                  >
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.mineShaft,
                      }}
                    >
                      IPO
                    </div>

                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.lynch,
                      }}
                    >
                      {company.ipoDate}
                    </div>
                  </div>
                )}

                {company.numberOfEmployees !== 0 && (
                  <div
                    css={{
                      display: "grid",
                      gridRowGap: 4,
                    }}
                  >
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.mineShaft,
                      }}
                    >
                      N° of Employees
                    </div>

                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.lynch,
                      }}
                    >
                      {numeral(company.numberOfEmployees).format("0,0").replace(/,/g, ".")}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div
              css={{
                display: "grid",
                gridAutoFlow: "column",
                gridAutoColumns: "max-content",
                gridColumnGap: 20,
              }}
            >
              {company.marketCapitalization && company.marketCapitalization.totalCapitalization > 0 && (
                <div
                  css={{
                    display: "grid",
                    gridRowGap: 4,
                  }}
                >
                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: "bold",
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.mineShaft,
                    }}
                  >
                    Market Cap
                  </div>

                  <div
                    title={String(company.marketCapitalization.totalCapitalization)}
                    css={{
                      display: "inline-flex",
                      fontSize: 14,
                      lineHeight: 1.33,
                      fontWeight: 500,
                      letterSpacing: 0.5,
                      color: palette.lynch,
                    }}
                  >
                    {formatMoneyValue(company.marketCapitalization.totalCapitalization, "long", "en-US")}
                  </div>
                </div>
              )}

              <div
                css={{
                  display: "grid",
                  gridRowGap: 4,
                }}
              >
                <div
                  css={{
                    fontSize: 14,
                    fontWeight: "bold",
                    lineHeight: 1.33,
                    letterSpacing: 0.5,
                    color: palette.mineShaft,
                  }}
                >
                  Exchange
                </div>

                <div
                  aria-label={company.exchange}
                  title={company.exchange}
                  css={{
                    display: "inline-flex",
                    fontSize: 14,
                    lineHeight: 1.33,
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    color: palette.lynch,
                  }}
                >
                  {/NASDAQ/i.test(company.exchange) && <NASDAQ width={67} height={19} />}
                  {/(NYSE|New York Stock Exchange)/i.test(company.exchange) && <NYSE width={19} height={19} />}
                </div>
              </div>
            </div>

            <div>
              <div
                css={{
                  display: "grid",
                  gridAutoFlow: "column",
                  gridAutoColumns: "max-content",
                  gridColumnGap: 20,
                }}
              >
                {company.address && (
                  <div
                    css={{
                      display: "grid",
                      gridRowGap: 4,
                    }}
                  >
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.mineShaft,
                      }}
                    >
                      Headquarters
                    </div>

                    <div
                      css={{
                        gridAutoFlow: "column",
                        gridAutoColumns: "max-content",
                        gridColumnGap: 8,
                        display: "inline-grid",
                      }}
                    >
                      <div
                        css={{
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: 1.33,
                          letterSpacing: 0.5,
                          color: palette.lynch,
                        }}
                      >
                        {company.address.city}
                      </div>

                      {company.address.country && (
                        <img
                          alt={company.address.country}
                          src={`https://s.fndm.to/svg/common/flags/${company.address.country}.svg`}
                          width={18}
                          height={18}
                          css={{
                            objectFit: "cover",
                            height: 18,
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {company.placeOfIncorporation && (
                  <div
                    css={{
                      display: "grid",
                      gridRowGap: 4,
                    }}
                  >
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.mineShaft,
                      }}
                    >
                      Incorporation
                    </div>

                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        color: palette.lynch,
                      }}
                    >
                      {company.placeOfIncorporation}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div
                css={{
                  display: "grid",
                  gridAutoFlow: "column",
                  gridAutoColumns: "max-content",
                  gridColumnGap: 20,
                }}
              >
                <div
                  css={{
                    display: "grid",
                    gridRowGap: 4,
                  }}
                >
                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: "bold",
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.mineShaft,
                    }}
                  >
                    Industry
                  </div>

                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.lynch,
                      display: "grid",
                      gridAutoColumns: "max-content",
                      gridAutoFlow: "column",
                      gridColumnGap: 8,
                    }}
                  >
                    <IndustrySector
                      assetType={company.isREIT ? IAssetType.AmericanReit : IAssetType.AmericanCompany}
                      industrySector={company.sector}
                    />
                  </div>
                </div>
              </div>
            </div>

            {company.websiteHref && (
              <div
                css={{
                  color: palette.lynch,
                  display: "grid",
                  gridAutoColumns: "max-content",
                  gridAutoFlow: "column",
                  gridColumnGap: 8,
                }}
                className="items-center"
              >
                {company.investorRelationsHref ? (
                  <a
                    title="Website "
                    target="_blank"
                    href={company.investorRelationsHref}
                    rel="noopener noreferrer"
                    className="inline-flex"
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.link,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Investor Relations
                  </a>
                ) : (
                  <a
                    title="Website "
                    target="_blank"
                    href={company.websiteHref}
                    rel="noopener noreferrer"
                    className="inline-flex"
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.link,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Website
                  </a>
                )}

                <span>•</span>

                <a
                  title="NASDAQ"
                  target="_blank"
                  href={company.nasdaqHref}
                  rel="noopener noreferrer"
                  className="inline-flex"
                  css={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: 1.33,
                    letterSpacing: 0.5,
                    color: palette.link,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  NASDAQ
                </a>

                <span>•</span>

                <a
                  title="SEC"
                  target="_blank"
                  href={company.edgarHref}
                  rel="noopener noreferrer"
                  css={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: 1.33,
                    letterSpacing: 0.5,
                    color: palette.link,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  SEC
                </a>
              </div>
            )}

            {company.shortBusinessDescription && (
              <div>
                <div
                  css={{
                    display: "grid",
                    gridRowGap: 4,
                  }}
                >
                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: "bold",
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.mineShaft,
                    }}
                  >
                    Description
                  </div>

                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.33,
                      letterSpacing: 0.8,
                      color: palette.baliHai,
                    }}
                  >
                    {company.shortBusinessDescription}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gridColumnGap: 30,
          gridRowGap: 52,
          alignItems: "center",
          margin: "0px auto",
          width: 1140,
        }}
      >
        <div
          css={{
            gridColumn: "2 / span 6",
            padding: 28,
            backgroundColor: "#ffffff",
            borderRadius: 4,
            boxShadow: "0 4px 6px 0 #33333333",
          }}
        >
          <h6
            css={{
              display: "flex",
              margin: 0,
              color: shades.greys.variants[2],
              fontWeight: "bold",
              fontSize: 14,
              fontFamily: "museo-sans-condensed",
              lineHeight: 1.33,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            Ratings
          </h6>

          <div css={{ marginTop: 8 }}>
            <Rating
              issuer={company}
              rankingPlacement={company.overallRankingPlacement}
              rankingRatingSummary={company.overallRankingRatingSummary}
            />
          </div>
        </div>
      </div>

      <AmericanCompanySections tickerSymbol={tickerSymbol} company={company} />

      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gridColumnGap: 30,
          gridRowGap: 52,
          alignItems: "center",
          margin: "0px auto",
          width: 1140,
        }}
      >
        <Liquidity key={company._id} asset={company} namespace={IAssetNamespace.Usa} tickerSymbols={[tickerSymbol]} />
      </div>

      <AmericanCompanyFinancials company={company} />

      <div css={{ marginTop: 40, marginBottom: 40 }}>
        {/* Page delimiters */}
        <div css={{ maxWidth: 1140, margin: "0px auto" }}>
          <div css={{ display: "grid", rowGap: 36 }}>
            {company.youtubeVideos.length > 0 && (
              <div>
                <YouTubeVideos
                  key={company._id}
                  youtubeVideos={user.isPremium ? company.youtubeVideos : company.youtubeVideos.slice(0, 1)}
                />
              </div>
            )}

            <TwoPassRender>
              {() => {
                return (
                  <AssetDiscussionGridContainer gridColumn="span 12">
                    <AssetDiscussion assetType={IAssetType.AmericanCompany} assetId={company._id} />
                  </AssetDiscussionGridContainer>
                );
              }}
            </TwoPassRender>
          </div>
        </div>
      </div>
    </div>
  );
}
